import React from "react";
import { Typography, Box } from "@mui/material";
import SimplePaper from "../../../components/SimplePaper";
import { MathComponent } from "mathjax-react";

const D6fromD1 = () => {
  const title = "Calculation of D6 (Shashtamsa) from D1 (Rasi)";

  const body = (
    <>
      <Typography variant="body1" paragraph>
        The D6 chart, or Shashtamsa chart, is used in Vedic astrology to analyze
        diseases, enemies, and obstacles in life. Below is the step-by-step
        process to calculate the D6 chart from the D1 (Rasi) chart.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Procedure to Calculate D6 (Shashtamsa) from D1 (Rasi)
      </Typography>

      <Typography variant="h5" gutterBottom>
        1. Identify the Planet's Degree in the D1 Chart
      </Typography>

      <Typography variant="body1" paragraph>
        First, identify the degree of the planet in the D1 chart. For example,
        if Mars is located at 11° Scorpio, this degree will be used for the
        calculation.
      </Typography>

      <Typography variant="h5" gutterBottom>
        2. Divide the Sign into Six Parts
      </Typography>

      <Typography variant="body1" paragraph>
        Each zodiac sign is divided into six equal parts, each spanning 5°. To
        find the Shashtamsa position of a planet, we divide its degree in the
        sign by 5.
      </Typography>

      <Box sx={{ textAlign: "center", margin: "1rem 0" }}>
        <MathComponent
          tex={String.raw`Shashtamsa \ Position = \frac{Degree \ in \ Sign}{5}`}
        />
      </Box>

      <Typography variant="body1" paragraph>
        Depending on the result, we can determine the section in which the
        planet lies:
      </Typography>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`0 \leq \frac{Degree \ in \ Sign}{5} < 1`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>first 5°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`1 \leq \frac{Degree \ in \ Sign}{5} < 2`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>second 5°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`2 \leq \frac{Degree \ in \ Sign}{5} < 3`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>third 5°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`3 \leq \frac{Degree \ in \ Sign}{5} < 4`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>fourth 5°</strong>.
        </Typography>
      </Box>

      <Box sx={{ textAlign: "left", margin: "1rem 0" }}>
        <Typography variant="body1" component="span">
          - If{" "}
        </Typography>
        <MathComponent
          tex={String.raw`4 \leq \frac{Degree \ in \ Sign}{5} < 5`}
          display={false}
        />
        <Typography variant="body1" component="span">
          , the planet is in the <strong>fifth 5°</strong>.
        </Typography>
      </Box>

      <Typography variant="h5" gutterBottom>
        3. Assign the Shashtamsa Based on the Planet's Degree
      </Typography>

      <Typography variant="body1" paragraph>
        <strong>First 5°:</strong> The planet is assigned to the{" "}
        <strong>same sign</strong> as the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Second 5°:</strong> The planet is assigned to the{" "}
        <strong>6th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Third 5°:</strong> The planet is assigned to the{" "}
        <strong>12th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fourth 5°:</strong> The planet is assigned to the{" "}
        <strong>8th sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Fifth 5°:</strong> The planet is assigned to the{" "}
        <strong>2nd sign</strong> from the D1 sign.
      </Typography>
      <Typography variant="body1" paragraph>
        <strong>Sixth 5°:</strong> The planet is assigned to the{" "}
        <strong>4th sign</strong> from the D1 sign.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Example: Calculating Mars at 11° Scorpio
      </Typography>

      <Typography variant="body1" paragraph>
        Scorpio is the D1 sign. Mars at 11° falls in the third 5° (between 10°
        and 15°). Therefore, it belongs to the <strong>12th sign</strong> from
        Scorpio, which is <strong>Libra</strong>
      </Typography>

      <Typography variant="h4" gutterBottom>
        Python Program to Calculate D6 (Shashtamsa) from D1
      </Typography>

      <Typography variant="body1" paragraph>
        Below is a Python program that performs the calculation of the D6
        (Shashtamsa) position from the D1 (Rasi) chart, using the planet's
        degree and sign in the D1 chart.
      </Typography>

      <Box
        component="pre"
        sx={{
          padding: "1rem",
          backgroundColor: "#f5f5f5",
          borderRadius: "5px",
        }}
      >
        <code>
          {`def calculate_shashtamsa(degree, sign):
    # List of zodiac signs in order
    zodiac_signs = ['Aries', 'Taurus', 'Gemini', 'Cancer', 'Leo', 'Virgo', 'Libra', 'Scorpio', 'Sagittarius', 'Capricorn', 'Aquarius', 'Pisces']

    # Get the index of the current sign in the zodiac cycle
    sign_index = zodiac_signs.index(sign)

    # Determine which Shashtamsa the planet is in based on its degree
    if degree <= 5:
        # First 5° belongs to the same sign
        shashtamsa_sign = zodiac_signs[sign_index]
    elif 5 < degree <= 10:
        # Second 5° belongs to the 6th sign from the current sign
        shashtamsa_sign = zodiac_signs[(sign_index + 5) % 12]
    elif 10 < degree <= 15:
        # Third 5° belongs to the 12th sign from the current sign
        shashtamsa_sign = zodiac_signs[(sign_index + 11) % 12]
    elif 15 < degree <= 20:
        # Fourth 5° belongs to the 8th sign from the current sign
        shashtamsa_sign = zodiac_signs[(sign_index + 7) % 12]
    elif 20 < degree <= 25:
        # Fifth 5° belongs to the 2nd sign from the current sign
        shashtamsa_sign = zodiac_signs[(sign_index + 1) % 12]
    else:
        # Sixth 5° belongs to the 4th sign from the current sign
        shashtamsa_sign = zodiac_signs[(sign_index + 3) % 12]

    return shashtamsa_sign

# Example usage
planet = "Mars"
degree = 11
sign = "Scorpio"

shashtamsa_sign = calculate_shashtamsa(degree, sign)
print(f"{planet} at {degree}° {sign} in the D1 chart is placed in {shashtamsa_sign} in the D6 chart.")`}
        </code>
      </Box>

      <Typography variant="body1" paragraph>
        The above Python program calculates the Shashtamsa for a planet based on
        its degree and sign in the D1 chart. The program divides the sign into
        six equal parts and assigns the Shashtamsa based on the planet's degree.
      </Typography>

      <Typography variant="h4" gutterBottom>
        Explanation of the Formula Used in the Python Program
      </Typography>

      <Typography variant="body1" paragraph>
        The program divides the sign into six parts: the first 5°, the second
        5°, the third 5°, the fourth 5°, the fifth 5°, and the sixth 5°. It
        assigns the Shashtamsa based on the planet's degree within the sign:
        <br />
        - First 5° belongs to the same sign.
        <br />
        - Second 5° belongs to the 6th sign from the D1 sign.
        <br />
        - Third 5° belongs to the 12th sign from the D1 sign.
        <br />
        - Fourth 5° belongs to the 8th sign from the D1 sign.
        <br />
        - Fifth 5° belongs to the 2nd sign from the D1 sign.
        <br />- Sixth 5° belongs to the 4th sign from the D1 sign.
      </Typography>

      <Typography variant="body1" paragraph>
        This method programmatically reproduces the manual calculation and can
        be easily adjusted for different planets, degrees, and signs.
      </Typography>
    </>
  );

  return <SimplePaper title={title} body={body} />;
};

export default D6fromD1;
